import { useState } from "react";
import {
  Box, Button, TextField, Typography, Alert, InputAdornment,
  IconButton, Paper, Link
} from "@mui/material";
import { Visibility, VisibilityOff, CheckCircle } from "@mui/icons-material";
import enhancedAuthService from "../../services/enhancedAuth";

// Password rules: at least 8 chars, 1 uppercase, 1 number, 1 special (non-alphanumeric) char
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const PASSWORD_EXAMPLE = 'Abcd1234!';

export default function ResetPasswordForm() {
  const [step,setStep]=useState<1|2>(1);
  const [email,setEmail]=useState("");
  const [otp,setOtp]=useState("");
  const [newPassword,setNewPassword]=useState("");
  const [confirmPassword,setConfirmPassword]=useState("");
  const [msg,setMsg]=useState<any>(null);
  const [loading,setLoading]=useState(false);
  const [success,setSuccess]=useState(false);
  const [show1,setShow1]=useState(false);
  const [show2,setShow2]=useState(false);

  const sendOtp = async () => {
    setLoading(true); setMsg(null);
    try{
      const res = await enhancedAuthService.requestPasswordReset({email});
      if(res.success){
        setMsg({type:"success",text:"OTP sent successfully to email"});
        setStep(2);
      } else setMsg({type:"error",text:res.error || res.message});
    }catch(e:any){
      setMsg({type:"error",text:"Error sending OTP"});
    }finally{ setLoading(false); }
  };

  const resetPassword = async () => {
    if (newPassword !== confirmPassword)
      return setMsg({ type: "error", text: "Passwords do not match" });

    // Validate password strength
    if (!PASSWORD_REGEX.test(newPassword)) {
      return setMsg({
        type: 'error',
        text: `Password must be at least 8 characters and include 1 uppercase letter, 1 number and 1 special character. Example: ${PASSWORD_EXAMPLE}`,
      });
    }

    setLoading(true); setMsg(null);
    try{
      const res = await enhancedAuthService.resetPassword({ email,otp,newPassword });

      if(res.success){
        setSuccess(true);
        setMsg({type:"success",text:"Password reset successful!"});
        setTimeout(()=>window.location.href="/login",1500);
      } else setMsg({type:"error",text:res.error || res.message});
      
    }catch(e:any){
      setMsg({type:"error",text:"Reset failed"});
    }finally{ setLoading(false); }
  };

  return(
    <Box sx={{
      minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center",
      background:"linear-gradient(160deg,#021526,#062743)"
    }}>

      <Paper sx={{
        width:"100%",maxWidth:440,p:5,textAlign:"center",
        background:"rgba(255,255,255,0.05)",
        backdropFilter:"blur(18px)",
        borderRadius:"18px",
        border:"1px solid rgba(0,255,150,.2)",
        boxShadow:"0 0 45px rgba(0,255,150,.25)",
        transition:"0.3s",
        "&:hover":{boxShadow:"0 0 65px rgba(0,255,150,.38)"},
      }}>

        {success && <CheckCircle sx={{fontSize:60,color:"#00FF88",mb:1}}/>}

        <Typography variant="h4"
          sx={{fontWeight:800,letterSpacing:.7,mb:1,color:"#00FF9D"}}>
          {success ? "Password Reset Successful" : "Reset Your Password"}
        </Typography>

        {/* Step Label */}
        {!success && (
          <Typography sx={{color:"#CCEADC",opacity:.65,mb:2}}>
            {step===1 && "Enter your registered email to receive OTP"}
            {step===2 && "Enter received OTP and create new password"}
          </Typography>
        )}

        {msg && <Alert severity={msg.type} sx={{mb:2,borderRadius:2}}>{msg.text}</Alert>}

        {/* STEP 1 : EMAIL */}
        {step===1 && !success && (
          <>
            <TextField fullWidth label="Enter Email" type="email"
              onChange={(e)=>setEmail(e.target.value)}
              sx={inputField}/>

            <Button fullWidth variant="contained" sx={buttonStyle} onClick={sendOtp}>
              {loading ? "Sending..." : "Send OTP"}
            </Button>
          </>
        )}

        {/* STEP 2 : OTP + PASSWORD */}
        {step===2 && !success && (
          <>
            <TextField fullWidth label="Enter OTP" type="text" value={otp}
              onChange={(e)=>setOtp(e.target.value)} sx={inputField}/>

            <TextField fullWidth label="New Password" type={show1?"text":"password"}
              onChange={(e)=>setNewPassword(e.target.value)}
              InputProps={{
                endAdornment:<InputAdornment position="end">
                  <IconButton onClick={()=>setShow1(!show1)}>
                    {show1? <VisibilityOff/>:<Visibility/>}
                  </IconButton>
                </InputAdornment>}}
              sx={inputField}/>

                {/* <Typography variant="caption" sx={{display:'block',textAlign:'left',color:'#CCEADC',mt:1}}>
                  Password must be at least 8 characters, include one uppercase letter, one number and one special character. Example: {PASSWORD_EXAMPLE}
                </Typography> */}

            <TextField fullWidth label="Confirm Password" type={show2?"text":"password"}
              onChange={(e)=>setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment:<InputAdornment position="end">
                  <IconButton onClick={()=>setShow2(!show2)}>
                    {show2? <VisibilityOff/>:<Visibility/>}
                  </IconButton>
                </InputAdornment>}}
              sx={inputField}/>

            <Button fullWidth variant="contained" sx={buttonStyle} onClick={resetPassword}>
              {loading?"Resetting...":"Reset Password"}
            </Button>
          </>
        )}

        <Link href="/login"
          sx={{display:"block",mt:3,color:"#00FFD1",textDecoration:"none",
          fontWeight:600,"&:hover":{textDecoration:"underline"}}}>
          {success ? "Go to Login →" : "← Back to Login"}
        </Link>

      </Paper>
    </Box>
  );
}

/* -------- Premium UI Styles -------- */

const inputField = {
  mb:2,mt:1,
  bgcolor:"rgba(255,255,255,.08)",
  borderRadius:"10px",
  input:{color:"#DFFFF4"},
  "& .MuiInputLabel-root":{color:"#9EFFCC"},
  "& fieldset":{borderColor:"rgba(0,255,160,.25)"},
  "&:hover fieldset":{borderColor:"#00FF9D"}
};

const buttonStyle = {
  mt:2,py:1.4,fontWeight:700,fontSize:16,letterSpacing:.6,
  background:"linear-gradient(90deg,#00FF9D,#00D6FF)",
  "&:hover":{opacity:.85}
};